/**
 * The operational schedule — what actually flew, or is planned to fly.
 *
 * Kept apart from `lib/reference` on purpose. Reference data is a vendor drop
 * plus a handful of operator decisions, changed a few rows at a time and
 * audited row by row. The schedule arrives in whole periods from AIMS, is
 * replaced in whole periods, and is orders of magnitude larger. The two have
 * different lifetimes, so they have different tables and different providers.
 */

/** What the file could not be joined to, per leg. */
export const LEG_ISSUES = [
  /** Departure or arrival station is not in the airport table. */
  'unknownStation',
  /** Registration is not in the fleet table. */
  'unknownTail',
  /** One end had no zone, so its instant came from the block time instead. */
  'blockDerived',
  /** Neither end could be placed on the clock. Never drawn. */
  'unplaceable',
] as const

export type LegIssue = (typeof LEG_ISSUES)[number]

/**
 * One flight leg. Placed on the clock in UTC, read by people in station local.
 *
 * Both are kept, and they are not redundant:
 *
 * - **UTC is what the leg is** (CODE-RULES 3.2). A bar can only be drawn, and
 *   two legs of a rotation can only be compared, on one absolute clock; 08:00
 *   at NRT and 08:00 at HAN are two hours apart.
 * - **Station local is what the file said**, and it is the reading the shift
 *   works in: STD 09:00 at SGN means nine in the morning at Tan Son Nhat. It
 *   is stored verbatim rather than recomputed, so the panel shows the
 *   operator's own numbers even where a station's zone is missing, newly
 *   created, or later corrected.
 */
export interface FlightLeg {
  /**
   * Natural key: operating date, flight number and the sector.
   *
   * Deliberately excludes the registration. Re-assigning a leg to another tail
   * is an edit to that leg, not a different leg, and a key carrying the tail
   * would turn every swap into one cancellation and one new flight.
   */
  key: string
  /** Operating day as the file states it, YYYY-MM-DD, local at the departure station. */
  operatingDate: string
  flightNumber: string
  /** J scheduled · C charter · P positioning · T training, as the file writes it. */
  serviceType: string
  /** Registration, or '' when the file leaves the leg unassigned. */
  regn: string
  /** Aircraft type as the file writes it (321, 320, 330). Not the fleet trio. */
  acType: string
  dep: string
  arr: string
  /** Scheduled departure / arrival, unix ms UTC. */
  stdUtc: number
  staUtc: number
  /** Actual or estimated, unix ms UTC. Null when the file leaves the cell empty. */
  etdUtc: number | null
  etaUtc: number | null
  /**
   * The same four readings on the station's own clock, HH:MM, exactly as the
   * file writes them — departure times at the departure station, arrival times
   * at the arrival station. Empty where the file left the cell empty.
   */
  stdLocal: string
  staLocal: string
  etdLocal: string
  etaLocal: string
  /** Block time in minutes, as the file states it. */
  blockMinutes: number
  /** Import that last wrote this leg. */
  batchId: string
  /**
   * Set when a later import for a period that covers this leg did not carry
   * it. BUSINESS-RULES 4.2: reference and schedule rows are never hard
   * deleted, so a withdrawn flight stays visible as withdrawn.
   */
  cancelledAt: string | null
  issues: LegIssue[]
}

/** A leg as read from a file, before any join to reference data. */
export interface RawLeg {
  operatingDate: string
  flightNumber: string
  serviceType: string
  regn: string
  acType: string
  dep: string
  arr: string
  /** Local wall clock at the station, HH:MM. */
  std: string
  sta: string
  etd: string
  eta: string
  blockMinutes: number
}

/** Inclusive range of operating dates an import declares, YYYY-MM-DD. */
export interface ImportScope {
  from: string
  to: string
}

/** One file, parsed and ready to be planned against what is already loaded. */
export interface ParsedFile {
  fileName: string
  /** Period the file declares in its own title, or the span of its rows. */
  scope: ImportScope
  /** True when the period came from the file's title rather than its rows. */
  scopeDeclared: boolean
  legs: RawLeg[]
  /** Rows the parser could not read, with the reason. */
  skipped: { row: number; reason: string }[]
}

/** What an import would do, counted before anything is written. */
export interface ImportPlan {
  added: number
  changed: number
  unchanged: number
  /** In scope, previously loaded, absent from the new file: withdrawn. */
  removed: number
  /** Outside every declared period. Not touched. */
  untouched: number
  /** Stations in the incoming files that the airport table does not carry. */
  unknownStations: string[]
  /** Registrations in the incoming files that the fleet table does not carry. */
  unknownTails: string[]
  /** Legs that could not be placed on the clock at all. */
  unplaceable: number
}

/** A completed import, kept so the board can say where its rows came from. */
export interface ImportBatch {
  id: string
  at: string
  by: string
  files: { fileName: string; scope: ImportScope; legs: number }[]
  plan: ImportPlan
}

export type ScheduleErrorCode =
  | 'unreadable_file'
  | 'unknown_layout'
  | 'empty_file'
  | 'storage_full'
  | 'unsupported_browser'
  | 'unknown'

export class ScheduleError extends Error {
  code: ScheduleErrorCode
  /** File the error belongs to, when it belongs to one. */
  fileName?: string
  constructor(code: ScheduleErrorCode, fileName?: string) {
    super(code)
    this.name = 'ScheduleError'
    this.code = code
    this.fileName = fileName
  }
}

/** Everything the board reads in one shot. */
export interface ScheduleSnapshot {
  legs: FlightLeg[]
  batches: ImportBatch[]
  /** False when the last write did not reach storage and lives only in memory. */
  persisted: boolean
  /** Whether the previous state is still available to restore. */
  canUndo: boolean
}

/**
 * Same contract rule as the auth and reference layers (CODE-RULES 3.3):
 * swapping this for a Supabase-backed provider must not touch a line of UI.
 */
export interface ScheduleProvider {
  readonly name: string
  load(): Promise<ScheduleSnapshot>
  /** Writes the legs an import produced, replacing each declared period. */
  apply(legs: FlightLeg[], batch: ImportBatch): Promise<ScheduleSnapshot>
  /** Restores the state from before the last import. */
  undo(): Promise<ScheduleSnapshot>
  /** Drops every loaded leg. */
  clear(): Promise<ScheduleSnapshot>
}
